import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, Download, Search, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, Select, inputCls, Spinner } from "../../components/ui-kit";

// Gestione dell'Archivio ricette DF (backend: routers_recipe_library.py,
// admin_router). Le ricette disattivate non compaiono ai clienti; quelle
// già caricate nei profili restano invariate.
const SOURCES = { df: "Ricette DF", clienti: "Da clienti DF", consulente: "Salvate da te" };
const searchable = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

function Tile({ label, value, active, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`min-h-[44px] rounded-xl border bg-white p-4 text-left transition-colors hover:bg-slate-50 ${active ? "border-navy ring-2 ring-navy/20" : "border-hairline"}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-heading text-3xl font-bold text-navy">{value}</div>
    </button>
  );
}

export default function RecipeLibraryAdmin() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("");
  const [source, setSource] = useState("");
  const [state, setState] = useState("");
  const [harvest, setHarvest] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/recipe-library").then(({ data: d }) => setData(d))
    .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setData({ recipes: [], sectors: [] }); });
  useEffect(() => { load(); }, []);

  const recipes = data?.recipes || [];
  const visible = useMemo(() => recipes.filter((r) =>
    (!search || searchable(r.name).includes(searchable(search)) || (r.ingredient_names || []).some((n) => searchable(n).includes(searchable(search)))) &&
    (!sector || r.sector === sector) && (!source || r.source === source) &&
    (!state || (state === "attive" ? r.active : state === "disattivate" ? !r.active : !r.verified))
  ), [recipes, search, sector, source, state]);
  const counts = useMemo(() => ({
    total: recipes.length,
    df: recipes.filter((r) => r.source === "df").length,
    clienti: recipes.filter((r) => r.source === "clienti").length,
    consulente: recipes.filter((r) => r.source === "consulente").length,
    off: recipes.filter((r) => !r.active).length,
  }), [recipes]);

  const patch = async (r, body) => {
    try {
      await api.patch(`/admin/recipe-library/${r.id}`, body);
      setData((d) => ({ ...d, recipes: d.recipes.map((x) => (x.id === r.id ? { ...x, ...body } : x)) }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const previewHarvest = async () => {
    setBusy("preview");
    try { const { data: d } = await api.post("/admin/recipe-library/harvest", { confirm: false }); setHarvest(d); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const confirmHarvest = async () => {
    setBusy("confirm");
    try {
      const { data: d } = await api.post("/admin/recipe-library/harvest", { confirm: true });
      toast.success(`${d.added} ${d.added === 1 ? "ricetta aggiunta" : "ricette aggiunte"} all’archivio`);
      setHarvest(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="recipe-library-admin">
      <div><p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-body">Configurazione clienti</p><h1 className="font-heading text-3xl font-bold">Archivio ricette DF</h1></div>

      <Card className="overflow-hidden border-2 border-blue-200 bg-gradient-to-r from-blue-50 via-white to-emerald-50 p-0">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4"><div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-blue-600 text-white shadow-sm"><Download className="h-6 w-6" /></div><div><h3 className="font-heading text-xl font-semibold text-navy">Raccogli dalle ricette dei clienti</h3><p className="mt-1 max-w-2xl text-sm text-slate-600">Aggiunge all’archivio le ricette approvate dei clienti che non ci sono ancora. Vengono copiati nome, ingredienti con allergeni, procedimento e conservazione: mai i dati dell’azienda, le quantità o i costi. Prima vedi l’elenco, poi confermi.</p></div></div>
          <Button variant="success" className="w-full sm:w-auto sm:shrink-0" loading={busy === "preview"} onClick={previewHarvest} data-testid="recipe-library-harvest">Vedi anteprima</Button>
        </div>
      </Card>

      {!data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Tile label="Totale" value={counts.total} active={!source && !state} onClick={() => { setSource(""); setState(""); }} />
            <Tile label="Ricette DF" value={counts.df} active={source === "df"} onClick={() => setSource(source === "df" ? "" : "df")} />
            <Tile label="Da clienti" value={counts.clienti} active={source === "clienti"} onClick={() => setSource(source === "clienti" ? "" : "clienti")} />
            <Tile label="Salvate da te" value={counts.consulente} active={source === "consulente"} onClick={() => setSource(source === "consulente" ? "" : "consulente")} />
            <Tile label="Disattivate" value={counts.off} active={state === "disattivate"} onClick={() => setState(state === "disattivate" ? "" : "disattivate")} />
          </div>

          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px_200px]">
            <div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca ricetta o ingrediente…" className={`${inputCls} bg-white pl-10`} data-testid="recipe-library-admin-search" /></div>
            <Select value={sector} onChange={(e) => setSector(e.target.value)} aria-label="Filtra per attività"><option value="">Tutte le attività</option>{data.sectors.map((s) => <option key={s}>{s}</option>)}</Select>
            <Select value={state} onChange={(e) => setState(e.target.value)} aria-label="Filtra per stato"><option value="">Tutti gli stati</option><option value="attive">Attive</option><option value="disattivate">Disattivate</option><option value="da_verificare">Da verificare</option></Select>
          </div>

          <p className="text-sm text-slate-600"><strong className="text-navy">{visible.length}</strong> ricette visualizzate</p>
          <Card className="divide-y divide-hairline">
            {visible.length === 0 && <div className="p-8 text-center text-slate-400"><BookOpen className="mx-auto mb-3 h-10 w-10 opacity-40" />Nessuna ricetta corrisponde ai filtri.</div>}
            {visible.map((r) => (
              <div key={r.id} className={`grid gap-3 p-4 lg:grid-cols-[minmax(0,1fr)_240px_auto] lg:items-center ${r.active ? "" : "bg-slate-50 opacity-70"}`} data-testid={`library-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-navy">{r.name}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{SOURCES[r.source] || r.source}</span>
                    {r.verified && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Verificata</span>}
                    {!r.active && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Disattivata</span>}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">{r.category}</div>
                  <div className="mt-1 text-xs text-slate-600">{(r.ingredient_names || []).join(", ")}</div>
                  {r.allergen_labels?.length > 0 && <div className="mt-1 text-xs text-red-600">Allergeni: {r.allergen_labels.join(", ")}</div>}
                </div>
                <Select value={r.sector} onChange={(e) => patch(r, { sector: e.target.value })} aria-label={`Attività di ${r.name}`}>
                  {data.sectors.map((s) => <option key={s}>{s}</option>)}
                </Select>
                <div className="flex flex-wrap gap-2">
                  <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-hairline px-3 text-sm">
                    <input type="checkbox" checked={!!r.verified} onChange={(e) => patch(r, { verified: e.target.checked })} className="h-4 w-4 accent-emerald-600" /> Verificata
                  </label>
                  <Button variant="outline" onClick={() => patch(r, { active: !r.active })} data-testid={`library-toggle-${r.id}`}>{r.active ? "Disattiva" : "Riattiva"}</Button>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {harvest && (
        <div className="fixed inset-0 z-50 grid place-items-end p-0 sm:place-items-center sm:p-6" data-testid="harvest-dialog">
          <div className="absolute inset-0 bg-navy/60" onClick={() => setHarvest(null)} />
          <Card className="relative flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-b-none p-0 sm:max-h-[90vh] sm:max-w-2xl sm:rounded-xl">
            <div className="flex items-start justify-between gap-2 border-b border-hairline p-5">
              <div><h3 className="font-heading text-lg font-semibold text-navy">Ricette dei clienti da aggiungere</h3><p className="mt-1 text-sm text-slate-600"><strong>{harvest.to_add}</strong> nuove · {harvest.already_in_library} già presenti nell’archivio (saltate). L’attività è proposta dalla categoria: la puoi cambiare dopo.</p></div>
              <button onClick={() => setHarvest(null)} className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Chiudi"><X className="h-5 w-5" /></button>
            </div>
            <div className="min-h-0 flex-1 divide-y divide-hairline overflow-y-auto">
              {harvest.preview.length === 0 && <div className="p-8 text-center text-sm text-slate-500">Nessuna ricetta nuova da aggiungere.</div>}
              {harvest.preview.map((p) => (
                <div key={p.name} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                  <div className="min-w-0"><div className="font-medium text-navy">{p.name}</div><div className="text-xs text-slate-500">{p.category || "Senza categoria"} · {p.ingredients} ingredienti</div></div>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{p.sector}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col-reverse gap-3 border-t border-hairline p-4 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setHarvest(null)}>Annulla</Button>
              <Button variant="success" disabled={!harvest.to_add} loading={busy === "confirm"} onClick={confirmHarvest} data-testid="harvest-confirm">Aggiungi {harvest.to_add} ricette all’archivio</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
